import memoizeOne, { type MemoizedFn } from "memoize-one"
import { JSX } from "react/jsx-runtime"

import { THEME } from "@/3514/theme"
import {
    EChapterAdvancedContentComponentAppearanceCount as EAdvancedComponentAppearance,
    EChapterComponentType
} from "@/3514/types"

import IntrinsicElements = JSX.IntrinsicElements

const getContentComponentHtmlClasses: MemoizedFn<(identifier: string) => string> = memoizeOne(function (
    identifier: string
): string {
    return `
        .${identifier} h3 {
          font-size: ${THEME.FONT_SIZES[24]}px;
          font-weight: ${THEME.FONT_WEIGHTS[300]};
          line-height: ${THEME.LINE_HEIGHTS[36]}px;
          color: ${THEME.COLORS.gray[100]};
          margin: 0;
        }
        .${identifier} h4 {
          font-size: ${THEME.FONT_SIZES[16]}px;
          font-weight: ${THEME.FONT_WEIGHTS[800]};
          line-height: ${THEME.LINE_HEIGHTS[24]}px;
          color: ${THEME.COLORS.gray[100]};
          margin: 0;
        }
        .${identifier} ol, 
        .${identifier} ul {
          list-style-type: revert;
          list-style-position: outside;
          margin: 0 0 0px 20px;
        }
        .${identifier} ol li:not(:last-child), 
        .${identifier} ul li:not(:last-child) {
          margin-bottom: 5px;
        }
        .${identifier} ol:not(:last-child), 
        .${identifier} ul:not(:last-child) {
          margin-bottom: 25px;
        }
        .${identifier} p:not(:last-child) {
          margin-bottom: 10px;
        }
        .${identifier} p, 
        .${identifier} span, 
        .${identifier} li {
          font-size: ${THEME.FONT_SIZES[16]}px;
          font-weight: ${THEME.FONT_WEIGHTS[300]};
          line-height: ${THEME.LINE_HEIGHTS[24]}px;
          color: ${THEME.COLORS.gray[100]};
          margin: 0;
        }
        .${identifier} a {
          font-size: ${THEME.FONT_SIZES[16]}px;
          font-weight: ${THEME.FONT_WEIGHTS[300]};
          line-height: ${THEME.LINE_HEIGHTS[24]}px;
          text-decoration: underline;
          color: ${THEME.COLORS.accent};
        }
        .${identifier} blockquote {
          padding-left: 40px;  
          padding-right: 40px;  
        }
        .${identifier} blockquote, 
        .${identifier} blockquote > * {
          font-size: ${THEME.FONT_SIZES[24]}px;
          font-weight: ${THEME.FONT_WEIGHTS[300]};
          line-height: ${THEME.LINE_HEIGHTS[36]}px;
          color: ${THEME.COLORS.gray[100]};
          text-align: center;
          margin: 0;
        }
        .${identifier} p > b, 
        .${identifier} p > strong, 
        .${identifier} p > i, 
        .${identifier} p > u {
          line-height: ${THEME.LINE_HEIGHTS[24]}px;
        }
        .${identifier} b, 
        .${identifier} strong {
          font-weight: ${THEME.FONT_WEIGHTS[800]};
        }
        .${identifier} br {
          display: block;
        }
        .${identifier} hr {
          margin: 15px 0;
          height: 1px;
          width: 100%;
          background-color: ${THEME.COLORS.blue[400]};
          border: 0;
        }
        .${identifier} table {
          table-layout: fixed;
          width: 100%;
        }
        .${identifier} table tbody {
          display: block;
          padding: 20px;
        }
        @media (max-width: 767px){
          .${identifier} table tbody {
            padding-left: 0;
            padding-right: 0;
          }
        }
        .${identifier} table th, 
        .${identifier} table td {
          width: auto;
        }
        .${identifier} table tr > th, 
        .${identifier} table tr > td {
          font-size: ${THEME.FONT_SIZES[14]}px;
          line-height: ${THEME.LINE_HEIGHTS[21]}px;
          color: ${THEME.COLORS.gray[100]};
        }
        .${identifier} table tr > td li {
          font-size: ${THEME.FONT_SIZES[14]}px;
          line-height: ${THEME.LINE_HEIGHTS[21]}px;
          color: ${THEME.COLORS.gray[100]};
        }
        .${identifier} table tr > th {
          text-align: center;
        }
        .${identifier} table tr > td {
          text-align: left;
        }
        .${identifier} table tr > th > strong {
          line-height: ${THEME.LINE_HEIGHTS[21]}px;
          display: block;  
        }
        .${identifier} table tr > td {
          padding: 10px;
          font-weight: ${THEME.FONT_WEIGHTS[300]};
          border: 1px solid ${THEME.COLORS.gray[400]};
          vertical-align: top;
        }
        .${identifier} table tr > td:first-child {
          padding-right: 20px;
        }
        .${identifier} table tr > td:last-child {
          padding-left: 20px;
        }
        .${identifier} table tr > th {
          width: 50%;
          padding: 10px 0;
          box-shadow: 0 0 0 10px white;
          background-clip: padding-box;
          font-weight: ${THEME.FONT_WEIGHTS[800]};
          border-left: 1px solid ${THEME.COLORS.gray[400]};
          border-right: 1px solid ${THEME.COLORS.gray[400]};
          border-top: none;
          border-bottom: none;
        }
        .${identifier} table tr > th:first-child {
          padding-right: 10px;
        }
        .${identifier} table tr > th:last-child {
          padding-left: 10px;
        }
        .${identifier} table tr > td:first-child, 
        .${identifier} table tr > th:first-child {
          border-left: none;
        }
        .${identifier} table tr > td:last-child, 
        .${identifier} table tr > th:last-child {
          border-right: none;
        }
        .${identifier} table tr:first-child > td, 
        .${identifier} table tr:first-child > th {
          border-top: none;
        }
        .${identifier} table tr:last-child > td {
          border-bottom: none;
        }
    `
})

const getAdvancedContentComponentGenericHTMLClasses: MemoizedFn<(identifier: string) => string> = memoizeOne(function (
    identifier: string
): string {
    return `
        .${identifier} p,
        .${identifier} span,
        .${identifier} a, 
        .${identifier} i, 
        .${identifier} u, 
        .${identifier} b, 
        .${identifier} strong, 
        .${identifier} li {
          font-size: ${THEME.FONT_SIZES[16]}px;
          font-weight: ${THEME.FONT_WEIGHTS[300]};
          line-height: ${THEME.LINE_HEIGHTS[24]}px;
        }
        .${identifier} p, 
        .${identifier} li, 
        .${identifier} span {
          color: ${THEME.COLORS.gray[100]};
          margin: 0;
        }
        .${identifier} a {
          color: ${THEME.COLORS.accent};
          text-decoration: underline;
        }
        .${identifier} b, 
        .${identifier} strong {
          font-weight: ${THEME.FONT_WEIGHTS[800]};
        }
        .${identifier} br {
          display: block;
        }
    `
})

const getAdvancedContentComponentFeatureHTMLClasses: MemoizedFn<(identifier: string) => string> = memoizeOne(function (
    identifier: string
): string {
    return `
        .${identifier} ul, .${identifier} ol { 
            margin-bottom: 0px; 
            margin-left: 20px;
            list-style-type: disc; 
            list-style-position: outside;
        }
        .${identifier} li:not(:last-child) {
            margin-bottom: 5px;
        }
    `
})

const advancedContentFeatureColorMap: { [A in EAdvancedComponentAppearance]: string } = {
    [EAdvancedComponentAppearance.First]: "bg-green-300",
    [EAdvancedComponentAppearance.Second]: "bg-yellow-200",
    [EAdvancedComponentAppearance.Third]: "bg-lightblue-300"
}

const contentHtmlAllowedTags: (keyof IntrinsicElements)[] = [
    // headings
    "h3",
    "h4",
    // usual text
    "p",
    "span",
    "u",
    "i",
    "b",
    "strong",
    // lists
    "ol",
    "ul",
    "li",
    // tables
    "table",
    "tr",
    "td",
    "br",
    "th",
    // sundry
    "hr",
    "blockquote",
    "a"
]

const SUPPORTED_COMPONENT_TYPES: EChapterComponentType[] = [
    EChapterComponentType.Content,
    EChapterComponentType.AdvancedContent,
    EChapterComponentType.CoachingMoment,
    EChapterComponentType.VideoVimeo,
    EChapterComponentType.QuestionGroup
]

const VALIDATED_COMPONENT_TYPES: EChapterComponentType[] = [
    EChapterComponentType.CoachingMoment,
    EChapterComponentType.QuestionGroup
]

const config = {
    getContentComponentHtmlClasses,
    getAdvancedContentComponentGenericHTMLClasses,
    getAdvancedContentComponentFeatureHTMLClasses,
    advancedContentFeatureColorMap,
    contentHtmlAllowedTags,
    SUPPORTED_COMPONENT_TYPES,
    VALIDATED_COMPONENT_TYPES
}

export { config as participantChapterUiConfig }
